import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type AlertType =
  | 'low_readiness'
  | 'sleep_decline'
  | 'hrv_drop'
  | 'missed_sync'
  | 'weight_change'
  | 'recovery_needed'
  | 'streak_at_risk'
  | 'inactivity';

export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface HealthAlert {
  id: string;
  clientId: string;
  organizationId: string;
  alertType: AlertType;
  severity: AlertSeverity;
  message: string;
  data: Record<string, unknown>;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  dismissed: boolean;
  createdAt: string;
  expiresAt: string;
  // Joined client info
  clientName?: string;
}

interface UseHealthAlertsOptions {
  includeAcknowledged?: boolean;
  clientId?: string;
}

export function useHealthAlerts(
  organizationId: string | undefined,
  options: UseHealthAlertsOptions = {}
) {
  const { includeAcknowledged = false, clientId } = options;
  const [alerts, setAlerts] = useState<HealthAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // featureAvailable becomes `false` when the underlying `health_alerts` table
  // doesn't exist in the DB (Postgres error 42P01). Callers use this to hide
  // the shell UI until the migration is applied in production.
  const [featureAvailable, setFeatureAvailable] = useState(true);

  const loadAlerts = useCallback(async () => {
    if (!organizationId) {
      setAlerts([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('health_alerts')
        .select(
          `
          *,
          clients:client_id (name)
        `
        )
        .eq('organization_id', organizationId)
        .eq('dismissed', false)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (!includeAcknowledged) {
        query = query.is('acknowledged_at', null);
      }

      if (clientId) {
        query = query.eq('client_id', clientId);
      }

      const { data, error: queryError } = await query;

      if (queryError) {
        // Table might not exist yet — swallow silently but flag so the UI hides.
        if (queryError.code === '42P01') {
          setFeatureAvailable(false);
          setAlerts([]);
          return;
        }
        throw queryError;
      }

      // Successful query — feature is available.
      setFeatureAvailable(true);

      setAlerts(
        (data || []).map((a) => ({
          id: a.id,
          clientId: a.client_id,
          organizationId: a.organization_id,
          alertType: a.alert_type as AlertType,
          severity: a.severity as AlertSeverity,
          message: a.message,
          data: a.data || {},
          acknowledgedAt: a.acknowledged_at,
          acknowledgedBy: a.acknowledged_by,
          dismissed: a.dismissed,
          createdAt: a.created_at,
          expiresAt: a.expires_at,
          clientName: (a.clients as { name: string } | null)?.name,
        }))
      );
    } catch (err) {
      console.error('Error loading health alerts:', err);
      // Detect "table doesn't exist" surfaced as a thrown error as well.
      const errCode = (err as { code?: string } | null)?.code;
      if (errCode === '42P01') {
        setFeatureAvailable(false);
      }
      setError('Failed to load health alerts');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, includeAcknowledged, clientId]);

  const acknowledgeAlert = useCallback(
    async (alertId: string) => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        const { error: updateError } = await supabase
          .from('health_alerts')
          .update({
            acknowledged_at: new Date().toISOString(),
            acknowledged_by: user?.id,
          })
          .eq('id', alertId);

        if (updateError) throw updateError;

        // Update local state
        setAlerts((prev) =>
          prev.map((a) =>
            a.id === alertId
              ? {
                  ...a,
                  acknowledgedAt: new Date().toISOString(),
                  acknowledgedBy: user?.id || null,
                }
              : a
          )
        );
      } catch (err) {
        console.error('Error acknowledging alert:', err);
        throw err;
      }
    },
    []
  );

  const dismissAlert = useCallback(async (alertId: string) => {
    try {
      const { error: updateError } = await supabase
        .from('health_alerts')
        .update({ dismissed: true })
        .eq('id', alertId);

      if (updateError) throw updateError;

      // Remove from local state
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
    } catch (err) {
      console.error('Error dismissing alert:', err);
      throw err;
    }
  }, []);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  // Count alerts by severity
  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;
  const warningCount = alerts.filter((a) => a.severity === 'warning').length;
  const infoCount = alerts.filter((a) => a.severity === 'info').length;

  return {
    alerts,
    loading,
    error,
    featureAvailable,
    reload: loadAlerts,
    acknowledgeAlert,
    dismissAlert,
    criticalCount,
    warningCount,
    infoCount,
    totalCount: alerts.length,
  };
}

// Function to manually generate alerts (can be called from cron or manually)
export async function generateHealthAlerts(organizationId: string) {
  const { data, error } = await supabase.rpc('generate_health_alerts', {
    p_organization_id: organizationId,
  });

  if (error) {
    console.error('Error generating health alerts:', error);
    throw error;
  }

  return data as number; // Returns count of alerts generated
}
